import { scry, scryImageUrl } from "../lib/scryClient";
import { useEffect, useMemo, useRef, useState } from "react";
import { searchScryfallSimple, toTradeItemFromScryfall } from "./tradeHelpers";
import type { TradeItem } from "./tradeTypes";

type Props = {
  placeholder?: string;
  onPick(item: TradeItem): void;
};

export default function ScrySearchInput({ placeholder = "Search Scryfall…", onPick }: Props) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const acRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    setBusy(true);
    const ac = new AbortController();
    acRef.current?.abort();
    acRef.current = ac;
    const t = setTimeout(async () => {
      try {
        const r = await searchScryfallSimple(q);
        setResults(r);
        setOpen(r.length > 0);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [q]);

  const visible = open && results.length > 0;

  return (
    <div className="relative w-full">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-100"
        onFocus={() => setOpen(results.length > 0)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        aria-label="Search Scryfall"
      />
      {busy && <div className="absolute right-2 top-2 text-xs opacity-60">…</div>}

      {/* Inline compact results — pushes content down, scrollable, ~5 rows visible */}
      {visible && (
        <div className="mt-2" role="listbox" aria-label="Scryfall results">
          <div className="max-h-[300px] overflow-y-auto rounded-md border border-slate-700 bg-gray-900 shadow ring-1 ring-black/5">
            <ul className="divide-y divide-slate-800">
              {results.map((sf, idx) => {
                const img =
                  sf?.image_uris?.small ||
                  (Array.isArray(sf?.card_faces) ? sf.card_faces[0]?.image_uris?.small : null) ||
                  (sf?.id
                    ? ` + scryImageUrl(sf.id, "small") + `
                    : "/images/cardback.png");
                const setName = (sf?.set_name || sf?.set || "").toUpperCase();
                const number = sf?.collector_number ? ` • #${sf.collector_number}` : "";
                const price = sf?.prices?.usd_foil || sf?.prices?.usd || "0.00";
                return (
                  <li key={sf?.id || `${sf?.name}-${idx}`}>
                    <button
                      type="button"
                      role="option"
                      className="group w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/5 focus:bg-white/10 focus:outline-none"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onPick(toTradeItemFromScryfall(sf));
                        setQ("");
                        setOpen(false);
                      }}
                    >
                      <img
                        src={img}
                        alt=""
                        className="h-14 w-10 object-cover rounded bg-black/50 flex-none transition-transform duration-200 ease-out group-hover:scale-110"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm text-white">{sf?.name}</div>
                        <div className="truncate text-xs text-slate-400">
                          {setName}
                          {number}
                        </div>
                      </div>
                      <div className="ml-auto text-xs text-gray-300">${price}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

